import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import {
    fetchConfiguraciones, createConfiguracion, updateConfiguracion, deleteConfiguracion,
    crearTarifaNombrada, actualizarTarifaNombrada, eliminarTarifaConReasignacion,
    crearCombinacion, actualizarCombinacion, eliminarCombinacion,
    type TarifaNombradaInput, type CombinacionInput,
} from '../api';
import { Configuracion } from "../../types/configuraciones";
import { useTenantCode } from './useTenantCode';
import { invalidarListasDeCobro } from './invalidaciones';

interface UpdateConfiguracionParams {
    id: string;
    data: Configuracion;
}


// Query hook para obtener configuraciones
export const useConfigurationsQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['configurations', tenantCode],
        queryFn: fetchConfiguraciones
    });
};

// Mutación para crear configuraciones
export const useCreateConfiguracionMutation = () => useMutation({
    mutationFn: createConfiguracion,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['configurations'] });
        toast.success('Configuracion creada exitosamente');
    },
    onError: () => {
        toast.error('Error al crear la configuracion');
    },
});

// Mutación para actualizar configuraciones
export const useUpdateConfiguracionMutation = () => useMutation({
    mutationFn: ({id, data}: UpdateConfiguracionParams) => updateConfiguracion(id, data),
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['configurations'] });
        toast.success('Configuracion actualizada exitosamente');
    },
    onError: () => {
        toast.error('Error al actualizar la configuracion');
    },
});

// ── Tarifas con nombre ──────────────────────────────────────────────────────
const mensajeError = (error: any, porDefecto: string) => error?.response?.data?.message || porDefecto;

const invalidarTrasTarifa = () => {
    queryClient.invalidateQueries({ queryKey: ['configurations'] });
    queryClient.invalidateQueries({ queryKey: ['cotizacion'] });
};

export const useCrearTarifaNombradaMutation = () => useMutation({
    mutationFn: ({ configId, data }: { configId: string; data: TarifaNombradaInput }) => crearTarifaNombrada(configId, data),
    onSuccess: () => {
        invalidarTrasTarifa();
        toast.success('Tarifa creada');
    },
    onError: (error: any) => toast.error(mensajeError(error, 'Error al crear la tarifa')),
});

// Con `dryRun` solo devuelve a quién le cambiaría el importe; sin él guarda y
// aplica. Los toasts los pone la pantalla, que sabe en cuál de los dos pasos está.
export const useActualizarTarifaNombradaMutation = () => useMutation({
    mutationFn: ({ configId, tarifaId, data }: { configId: string; tarifaId: string; data: TarifaNombradaInput & { dryRun?: boolean } }) =>
        actualizarTarifaNombrada(configId, tarifaId, data),
    onSuccess: (_data, variables) => {
        if (!variables.data.dryRun) {
            invalidarTrasTarifa();
            queryClient.invalidateQueries({ queryKey: ['client'] });
            invalidarListasDeCobro();
        }
    },
    onError: (error: any) => toast.error(mensajeError(error, 'Error al actualizar la tarifa')),
});

// Borrar con reasignación de sus clientes. Con `dryRun` (por defecto) solo
// devuelve qué pasaría; la pantalla pone los toasts.
export const useEliminarTarifaNombradaMutation = () => useMutation({
    mutationFn: ({ configId, tarifaId, dryRun = true, destino = null }: { configId: string; tarifaId: string; dryRun?: boolean; destino?: string | null }) =>
        eliminarTarifaConReasignacion(configId, tarifaId, { dryRun, destino }),
    onSuccess: (_data, variables) => {
        if (variables.dryRun === false) {
            invalidarTrasTarifa();
            queryClient.invalidateQueries({ queryKey: ['client'] });
            invalidarListasDeCobro();
        }
    },
    onError: (error: any) => toast.error(mensajeError(error, 'Error al eliminar la tarifa'), { autoClose: 8000 }),
});

// ── Combinaciones de tarifas ("Híbrido") ────────────────────────────────────
// Mismo patrón que las tarifas: `dryRun` solo calcula; la pantalla pone los toasts.

export const useCrearCombinacionMutation = () => useMutation({
    mutationFn: ({ configId, data }: { configId: string; data: CombinacionInput }) => crearCombinacion(configId, data),
    onSuccess: () => {
        invalidarTrasTarifa();
        toast.success('Combinación creada');
    },
    onError: (error: any) => toast.error(mensajeError(error, 'Error al crear la combinación'), { autoClose: 8000 }),
});

export const useActualizarCombinacionMutation = () => useMutation({
    mutationFn: ({ configId, combinacionId, data }: { configId: string; combinacionId: string; data: CombinacionInput & { dryRun?: boolean } }) =>
        actualizarCombinacion(configId, combinacionId, data),
    onSuccess: (_data, variables) => {
        if (!variables.data.dryRun) {
            invalidarTrasTarifa();
            queryClient.invalidateQueries({ queryKey: ['client'] });
            invalidarListasDeCobro();
        }
    },
    onError: (error: any) => toast.error(mensajeError(error, 'Error al actualizar la combinación'), { autoClose: 8000 }),
});

export const useEliminarCombinacionMutation = () => useMutation({
    mutationFn: ({ configId, combinacionId, dryRun = true }: { configId: string; combinacionId: string; dryRun?: boolean }) =>
        eliminarCombinacion(configId, combinacionId, { dryRun }),
    onSuccess: (_data, variables) => {
        if (variables.dryRun === false) {
            invalidarTrasTarifa();
            queryClient.invalidateQueries({ queryKey: ['client'] });
            invalidarListasDeCobro();
        }
    },
    onError: (error: any) => toast.error(mensajeError(error, 'Error al eliminar la combinación'), { autoClose: 8000 }),
});

// Mutación para eliminar configuraciones
export const useDeleteConfiguracionMutation = () => useMutation({
    mutationFn: deleteConfiguracion,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['configurations'] });
        toast.success('Configuracion eliminada exitosamente');
    },
    onError: () => {
        toast.error('Error al eliminar la configuracion');
    },
});